import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Trash2, Pause, Play, Plus, Wallet, Zap } from "lucide-react";
import api, { formatApiError } from "../api/client";
import { formatUSD } from "../lib/format";

const MAX_BUDGET = 5_000_000_000; // $5B cap
const BUDGET_PRESETS = [
    { label: "$10M", v: 10_000_000 },
    { label: "$100M", v: 100_000_000 },
    { label: "$500M", v: 500_000_000 },
    { label: "$1B", v: 1_000_000_000 },
    { label: "$5B", v: 5_000_000_000 },
];
const MARGIN_PRESETS = [
    { label: "$10k", v: 10_000 },
    { label: "$50k", v: 50_000 },
    { label: "$100k", v: 100_000 },
    { label: "$500k", v: 500_000 },
    { label: "$1M", v: 1_000_000 },
    { label: "$10M", v: 10_000_000 },
];

const formatBig = (n) => {
    const v = +n || 0;
    if (v >= 1e9) return `$${(v / 1e9).toFixed(v >= 1e10 ? 0 : 2)}B`;
    if (v >= 1e6) return `$${(v / 1e6).toFixed(v >= 1e7 ? 0 : 2)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k`;
    return `$${v.toFixed(2)}`;
};

export default function Bots() {
    const [bots, setBots] = useState([]);
    const [name, setName] = useState("Verslan-MM-2");
    const [coin, setCoin] = useState("BTC");
    const [universal, setUniversal] = useState(false);
    const [autoTopup, setAutoTopup] = useState(false);
    const [maxMargin, setMaxMargin] = useState("100000");
    const [marginBudget, setMarginBudget] = useState("10000000");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { const r = await api.get("/bots"); setBots(r.data); } catch {}
    };
    useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, []);

    const create = async () => {
        const mpf = parseFloat(maxMargin);
        const mb = parseFloat(marginBudget);
        if (!mpf || mpf <= 0) return toast.error("Margin per fill must be > 0");
        if (!mb || mb <= 0) return toast.error("Margin budget must be > 0");
        if (mb > MAX_BUDGET) return toast.error(`Margin budget capped at ${formatBig(MAX_BUDGET)}`);
        if (mpf > mb) return toast.error("Margin per fill can't exceed total budget");
        setBusy(true);
        try {
            await api.post("/bots", {
                name,
                coin: universal ? "*" : coin.toUpperCase(),
                max_margin_per_fill: mpf,
                margin_budget: mb,
                auto_topup: autoTopup || universal,
                enabled: true,
            });
            toast.success(`${name} spawned`, {
                description: `${universal ? "Universal · all pairs" : coin.toUpperCase()} · ${formatBig(mb)} budget · ${formatBig(mpf)} / fill${autoTopup || universal ? " · auto top-up" : ""}`,
            });
            load();
        } catch (e) {
            toast.error("Bot creation failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally { setBusy(false); }
    };

    const toggle = async (b) => {
        try { await api.patch(`/bots/${b.id}`, { enabled: !b.enabled }); load(); }
        catch (e) { toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) }); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this bot? Its wallet and any open orders will also be removed.")) return;
        try { await api.delete(`/bots/${id}`); load(); }
        catch (e) { toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) }); }
    };
    const topUp = async (b) => {
        const next = window.prompt(`Set new margin budget for ${b.name} (USDC, max $5B):`, String(b.margin_budget || 0));
        if (next == null) return;
        const v = parseFloat(next);
        if (!v || v <= 0 || v > MAX_BUDGET) return toast.error(`Enter a value between 1 and ${formatBig(MAX_BUDGET)}`);
        try { await api.patch(`/bots/${b.id}`, { margin_budget: v }); toast.success(`Budget set to ${formatBig(v)}`); load(); }
        catch (e) { toast.error("Top-up failed", { description: formatApiError(e.response?.data?.detail) }); }
    };

    const totalBudget = bots.reduce((s, b) => s + (+b.margin_budget || +b.balance || 0), 0);
    const totalLocked = bots.reduce((s, b) => s + (+b.locked || 0), 0);
    const totalFills = bots.reduce((s, b) => s + (+b.fills || 0), 0);

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Liquidity</div>
                    <h1 className="text-3xl font-medium tracking-tighter mt-2">BUBE Liquidity Bots</h1>
                    <p className="text-zinc-400 mt-2 max-w-2xl">
                        Bots auto-take the opposite side of any open BUBE order so solo testnet users always
                        find a counterparty. Sizing is <span className="text-cyan-400">margin-based</span> — at
                        50–100× leverage, $100M of bot margin underwrites $5–10B of notional.
                    </p>
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Tile label="Active bots" value={String(bots.length)} accent="text-cyan-400" />
                    <Tile label="Total margin budget" value={formatBig(totalBudget)} />
                    <Tile label="Locked in matches" value={formatBig(totalLocked)} accent="text-amber-400" />
                    <Tile label="Total fills" value={totalFills.toLocaleString()} accent="text-emerald-400" />
                </div>

                {/* Spawn form */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Plus className="w-4 h-4 text-cyan-400" />
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-300">Spawn new bot</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Name">
                            <input data-testid="bot-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                        </Field>
                        <Field label={universal ? "Coin (universal · all pairs)" : "Coin"}>
                            <input
                                data-testid="bot-coin"
                                value={universal ? "* (all pairs)" : coin}
                                onChange={(e) => setCoin(e.target.value)}
                                disabled={universal}
                                className={`${inputCls} ${universal ? "opacity-50" : ""}`}
                            />
                        </Field>
                    </div>

                    {/* Universal & auto-topup toggles */}
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        <label
                            data-testid="bot-universal-toggle"
                            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-sm border cursor-pointer transition-colors ${
                                universal ? "border-violet-400/70 bg-violet-500/10" : "border-[#1F1F24] hover:border-zinc-700 bg-[#050507]"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={universal}
                                onChange={(e) => setUniversal(e.target.checked)}
                                className="mt-1 accent-violet-400"
                            />
                            <div>
                                <div className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${universal ? "text-violet-300" : "text-zinc-300"}`}>
                                    Universal · all pairs
                                </div>
                                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                                    Fills BTC, ETH, HYPE, every single and multi swap. Same wallet, all markets.
                                </div>
                            </div>
                        </label>
                        <label
                            data-testid="bot-autotopup-toggle"
                            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-sm border cursor-pointer transition-colors ${
                                autoTopup || universal ? "border-cyan-400/70 bg-cyan-500/10" : "border-[#1F1F24] hover:border-zinc-700 bg-[#050507]"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={autoTopup || universal}
                                disabled={universal}
                                onChange={(e) => setAutoTopup(e.target.checked)}
                                className="mt-1 accent-cyan-400"
                            />
                            <div>
                                <div className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${autoTopup || universal ? "text-cyan-300" : "text-zinc-300"}`}>
                                    Auto top-up · unlimited notionals
                                </div>
                                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                                    Wallet auto-deposits whatever margin an incoming order needs. Bot will never run out of capital.
                                </div>
                            </div>
                        </label>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <Field label="Margin per fill (USDC)" hint={(autoTopup || universal) ? "cap is enforced even with auto top-up" : "cap on margin locked per single counter-fill"}>
                            <input
                                data-testid="bot-max-margin"
                                type="number" min={1}
                                value={maxMargin}
                                onChange={(e) => setMaxMargin(e.target.value)}
                                className={inputCls}
                            />
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {MARGIN_PRESETS.map((p) => (
                                    <button
                                        key={p.label}
                                        data-testid={`bot-margin-preset-${p.v}`}
                                        type="button"
                                        onClick={() => setMaxMargin(String(p.v))}
                                        className={presetCls(parseFloat(maxMargin) === p.v)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Margin budget (USDC)" hint={(autoTopup || universal) ? "starting balance · auto top-up will lift on demand" : "total margin the bot's wallet can deploy · max $5B"}>
                            <input
                                data-testid="bot-margin-budget"
                                type="number" min={1} max={MAX_BUDGET}
                                value={marginBudget}
                                onChange={(e) => setMarginBudget(e.target.value)}
                                className={inputCls}
                            />
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {BUDGET_PRESETS.map((p) => (
                                    <button
                                        key={p.label}
                                        data-testid={`bot-budget-preset-${p.v}`}
                                        type="button"
                                        onClick={() => setMarginBudget(String(p.v))}
                                        className={presetCls(parseFloat(marginBudget) === p.v)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </div>

                    {/* Capacity preview */}
                    <div className="mt-4 bg-[#050507] border border-[#1F1F24] rounded-sm p-3 text-[11px] font-mono">
                        <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Capacity preview</div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                            <Stat label="Per-fill margin" value={formatBig(parseFloat(maxMargin))} accent="text-cyan-400" />
                            <Stat label="Notional / fill @ 50×" value={formatBig((parseFloat(maxMargin) || 0) * 50)} accent="text-emerald-400" />
                            <Stat label="Notional / fill @ 100×" value={formatBig((parseFloat(maxMargin) || 0) * 100)} accent="text-emerald-400" />
                            <Stat label="Total budget" value={formatBig(parseFloat(marginBudget))} />
                            <Stat label="Concurrent fills" value={`≈ ${Math.floor((parseFloat(marginBudget) || 0) / (parseFloat(maxMargin) || 1)).toLocaleString()}`} accent="text-amber-400" />
                        </div>
                    </div>

                    <button
                        data-testid="bot-spawn-button"
                        disabled={busy}
                        onClick={create}
                        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-semibold text-sm rounded-sm transition-colors"
                    >
                        <Bot className="w-4 h-4" />
                        {busy ? "Spawning…" : "Spawn bot →"}
                    </button>
                </div>

                {/* Table */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#1F1F24] flex justify-between items-baseline">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Active bots</div>
                            <div className="text-sm font-medium font-mono tracking-tight mt-0.5">{bots.length} running</div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[820px]" data-testid="bots-table">
                            <thead>
                                <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                                    {["Name", "Coin", "Margin / fill", "Budget", "Locked", "Free", "Fills", "Status", ""].map((h) => (
                                        <th key={h} className="text-left px-4 py-2 font-normal">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bots.length === 0 && (
                                    <tr><td colSpan={9} className="text-center py-8 text-[11px] font-mono text-zinc-600">no bots — spawn one above</td></tr>
                                )}
                                {bots.map((b) => {
                                    const free = (b.balance || 0) - (b.locked || 0);
                                    const utilization = b.balance ? ((b.locked || 0) / b.balance) * 100 : 0;
                                    return (
                                        <tr key={b.id} className="border-t border-[#1F1F24] font-mono text-xs">
                                            <td className="px-4 py-2 text-zinc-100">
                                                {b.name}
                                                {b.auto_topup && <span className="ml-1.5 text-[8px] font-mono uppercase tracking-[0.16em] text-cyan-400 border border-cyan-500/40 bg-cyan-500/10 px-1 py-0.5 rounded-sm">auto</span>}
                                            </td>
                                            <td className="px-4 py-2">
                                                {b.coin === "*" ? (
                                                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-violet-300 border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 rounded-sm">ALL</span>
                                                ) : (
                                                    <span className="text-cyan-400">{b.coin}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 tabular-nums">{formatBig(b.max_margin_per_fill || b.max_notional * 0.02)}</td>
                                            <td className="px-4 py-2 tabular-nums">{formatBig(b.margin_budget || b.balance)}</td>
                                            <td className="px-4 py-2 text-amber-400 tabular-nums">
                                                {formatUSD(b.locked || 0)}
                                                <span className="text-[9px] text-zinc-500 ml-1">{utilization.toFixed(1)}%</span>
                                            </td>
                                            <td className="px-4 py-2 text-emerald-400/80 tabular-nums">{formatUSD(free)}</td>
                                            <td className="px-4 py-2 tabular-nums">{b.fills}</td>
                                            <td className="px-4 py-2">
                                                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${b.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-500/20 text-zinc-500"}`}>
                                                    {b.enabled ? "RUNNING" : "PAUSED"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-right whitespace-nowrap">
                                                <button data-testid={`bot-topup-${b.id}`} onClick={() => topUp(b)} title="Resize margin budget" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 mr-3">
                                                    <Wallet className="w-3 h-3" /> Budget
                                                </button>
                                                <button data-testid={`bot-toggle-${b.id}`} onClick={() => toggle(b)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-cyan-400 mr-3">
                                                    {b.enabled ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Start</>}
                                                </button>
                                                <button data-testid={`bot-delete-${b.id}`} onClick={() => del(b.id)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-400 hover:text-rose-300">
                                                    <Trash2 className="w-3 h-3" /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="text-[10px] font-mono text-zinc-600 flex items-start gap-2">
                    <Zap className="w-3 h-3 mt-0.5 shrink-0 text-amber-400" />
                    <span>
                        Bots match orders where <span className="text-zinc-300">order margin ≤ bot.margin_per_fill</span> AND
                        <span className="text-zinc-300"> bot.free ≥ order.margin</span>. A $100M margin budget at $1M / fill underwrites
                        100 concurrent positions; at 50× that's <span className="text-emerald-400">$5B notional</span> of bookable liquidity.
                    </span>
                </div>
            </div>
        </div>
    );
}

const inputCls =
    "w-full bg-[#050507] border border-[#1F1F24] focus:border-cyan-400/80 rounded-sm px-3 py-2 font-mono text-sm text-zinc-100 outline-none transition-colors tabular-nums";

const presetCls = (active) =>
    `px-2 py-0.5 text-[10px] font-mono rounded-sm border transition-colors ${
        active
            ? "border-cyan-400/80 text-cyan-300 bg-cyan-500/10"
            : "border-[#1F1F24] text-zinc-500 hover:text-zinc-200 hover:border-zinc-600"
    }`;

const Field = ({ label, hint, children }) => (
    <div className="grid gap-1.5">
        <div className="flex items-baseline justify-between">
            <span className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400">{label}</span>
            {hint && <span className="text-[9px] font-mono text-zinc-600 lowercase">{hint}</span>}
        </div>
        {children}
    </div>
);

const Tile = ({ label, value, accent = "text-zinc-100" }) => (
    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-4">
        <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
        <div className={`mt-1 font-mono text-lg tabular-nums ${accent}`}>{value}</div>
    </div>
);

const Stat = ({ label, value, accent = "text-zinc-100" }) => (
    <div className="flex items-baseline gap-1.5">
        <span className="text-[9.5px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
        <span className={`tabular-nums ${accent}`}>{value}</span>
    </div>
);
